import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { event } from '@uhg-abyss/web/tools/event';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { isEmpty } from 'lodash';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Label } from '@uhg-abyss/web/ui/Label';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { validatePermissions } from '../../../../redux/resources/user/helpers';
import { MppSection } from './MppSection';
import { getFeature } from '../../../../redux/tools/features';
import { useRegisterEventListener } from '@uhg-abyss/parcels/hooks/useRegisterEventListener';
import { getDomain } from '@web/tools/getDomain';

const PaddedContainer = styled('div', {
  padding: '10px 0px',
});

const FlexDiv = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  '@media (min-width: 984px)': {
    gap: '15px',
  },
  '@media (max-width: 983px)': {
    marginTop: '-19px',
    gap: '5px',
  },
});

const StyledIconMessageContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyConent: 'center',
});
const StyledVerticalCenter = styled('div', {
  marginBottom: '3%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});
const StyledCheckbox = styled(Checkbox, {
  '&.abyss-checkbox-label': {
    fontWeight: 'normal',
    marginBottom: '3%',
  },
  '@media (max-width: 983px)': {
    '&.abyss-checkbox-label': {
      marginBottom: '6%',
    },
  },
});

const StyledH4 = styled('h4', {
  fontFamily: 'AbyssEnterpriseSansVF, Arial, sans-serif',
  color: '$primary1',
});

const getTotalCounts = (sectionCounts, appPermissions) => {
  const keys = Object.keys(sectionCounts);
  let totalCount = 0;
  keys.forEach((key) => {
    const count = Number(sectionCounts[key]?.count ?? 0);
    totalCount += appPermissions[key] ? count : 0;
  });
  return totalCount;
};

const defaultAppPermissions = {
  admissionDischarge: false,
  claims: false,
  doclib: false,
  mpp: false,
  paan: false,
  culturalCompetency: false,
};

export const ActionRequiredWidget = () => {
  const trackitDomain = getDomain('sde-trackit', 3400);
  const docLibDomain = getDomain('sde-documentlibrary', 3500);
  const { data, events } = usePropsContext();
  const { user, provider } = data;
  const [isRequiredOnlyChecked, setIsRequiredOnlyChecked] = useState(true);
  const [countsBySection, setCountsBySection] = useState({
    claims: { count: 0, error: false },
    culturalCompetency: { count: 0, error: false },
    doclib: { count: 0, error: false },
    admissionDischarge: { count: 0, error: false },
    mpp: { count: 0, error: false },
    paan: { count: 0, error: false },
    ssbci: { count: 0, error: false },
    memberReferrals: { count: 0, error: false },
  });
  const [isAllLoaded, setIsAllLoaded] = useState(false);
  const [showAllCaughtUpText, setShowAllCaughtUpText] = useState(false);
  const failedCallsRef = useRef(0);
  const [isClaimsLoading, setIsClaimsLoading] = useState(false);
  const [isClaimsEmpty, setIsClaimsEmpty] = useState(false);
  const [isPaanLoading, setIsPaanLoading] = useState(false);
  const [isPaanEmpty, setIsPaanEmpty] = useState(false);
  const [isDocLibLoading, setIsDocLibLoading] = useState(false);
  const [isDocLibEmpty, setIsDocLibEmpty] = useState(false);
  const [isAdmitDischargeErLoading, setIsAdmitDischargeErLoading] =
    useState(false);
  const [isAdmitDischargeEmpty, setIsAdmitDischargeEmpty] = useState(false);
  const [isMppLoading, setIsMppLoading] = useState(false);
  const [isCulturalCompetencyLoading, setIsCulturalCompetencyLoading] =
    useState(false);
  const [isCulturalCompetencyEmpty, setIsCulturalCompetencyEmpty] =
    useState(false);
  const [isSsbciLoading, setIsSsbciLoading] = useState(false);
  const [isSsbciEmpty, setIsSsbciEmpty] = useState(false);
  const [isMemberReferralEmpty, setIsMemberReferralsEmpty] = useState(false);
  const [isMemberReferralsLoading, setIsMemberReferralsLoading] =
    useState(false);

  const [showFullError, setShowFullError] = useState(false);
  const [showPartialError, setShowPartialError] = useState(false);
  const [appPermissions, setAppPermissions] = useState(defaultAppPermissions);

  const renderClaimsParcel = getFeature('ACTION_REQUIRED_CLAIMS_SECTION');
  const renderCulturalParcel = getFeature('ACTION_REQUIRED_CULT_COMP_SECTION');
  const renderDischargeEmergencyParcel = getFeature(
    'ACTION_REQUIRED_AD_DIS_ER_SECTION'
  );
  const renderPaanParcel = getFeature('ACTION_REQUIRED_PRIOR_AUTH_SECTION');
  const renderMppParcel = getFeature('ACTION_REQUIRED_MPP_SECTION');
  const renderDoclibParcel = getFeature(
    'ACTION_REQUIRED_DOC_TEAMS_VIEW_SECTION'
  );
  const renderSsbciParcel = getFeature('ACTION_REQUIRED_SSBCI_SECTION');
  const renderMemberReferralParcel = getFeature(
    'ACTION_REQUIRED_MEMBER_REFERRALS_SECTION'
  );
  const isCulturalCompetencyToggle = getFeature('TRACKIT_CULTURAL_COMPETENCY');
  const isPaanToggle = getFeature('TRACKIT_PAAN_ADDITIONAL_TAB');
  const isDocLibToggle = getFeature('DOCVAULT_TEAMS_VIEW');

  const handleSetIsCulturalCompetencyLoading = useCallback((event) => {
    setIsCulturalCompetencyLoading(event.detail);
  }, []);
  const handleSetIsCulturalCompetencyEmpty = useCallback((event) => {
    setIsCulturalCompetencyEmpty(event.detail);
  }, []);
  const handleSetClaimsIsEmpty = useCallback((event) => {
    setIsClaimsEmpty(event.detail);
  }, []);
  const handleSetClaimsIsLoading = useCallback((event) => {
    setIsClaimsLoading(event.detail);
  }, []);
  const handleSetAdmitDischargeIsEmpty = useCallback((event) => {
    setIsAdmitDischargeEmpty(event.detail);
  }, []);
  const handleSetAdmitDischargeIsLoading = useCallback((event) => {
    setIsAdmitDischargeErLoading(event.detail);
  }, []);
  const handleSetPaanIsEmpty = useCallback((event) => {
    setIsPaanEmpty(event.detail);
  }, []);
  const handleSetCountsBySection = useCallback((event) => {
    const { section, count, error } = event.detail;
    setCountsBySection((prevCounts) => {
      const updatedCounts = {
        ...prevCounts,
        [section]: { count, error },
      };
      return updatedCounts;
    });
  }, []);
  const handlePaanIsLoading = useCallback((event) => {
    setIsPaanLoading(event.detail);
  }, []);
  const handleIsDocLibLoading = useCallback((event) => {
    setIsDocLibLoading(event.detail);
  }, []);
  const handleIsDocLibEmpty = useCallback((event) => {
    setIsDocLibEmpty(event.detail);
  }, []);
  const handleIsSsbciLoading = useCallback((event) => {
    setIsSsbciLoading(event.detail);
  }, []);
  const handleIsSsbciEmpty = useCallback((event) => {
    setIsSsbciEmpty(event.detail);
  }, []);
  const handleIsMemberReferralsLoading = useCallback((event) => {
    setIsMemberReferralsLoading(event.detail);
  }, []);
  const handleIsMemberReferralsEmpty = useCallback((event) => {
    setIsMemberReferralsEmpty(event.detail);
  }, []);

  useRegisterEventListener(
    'setIsCulturalCompetencyLoading',
    handleSetIsCulturalCompetencyLoading
  );
  useRegisterEventListener(
    'setIsCulturalCompetencyEmpty',
    handleSetIsCulturalCompetencyEmpty
  );
  useRegisterEventListener('setClaimsIsEmpty', handleSetClaimsIsEmpty);
  useRegisterEventListener('setClaimsIsLoading', handleSetClaimsIsLoading);
  useRegisterEventListener(
    'setAdmitDischargeIsEmpty',
    handleSetAdmitDischargeIsEmpty
  );
  useRegisterEventListener(
    'setAdmitDischargeIsLoading',
    handleSetAdmitDischargeIsLoading
  );

  useRegisterEventListener('setPaanIsEmpty', handleSetPaanIsEmpty);
  useRegisterEventListener('setCountsBySection', handleSetCountsBySection);
  useRegisterEventListener('setIsPaanLoading', handlePaanIsLoading);
  useRegisterEventListener('setIsDocLibLoading', handleIsDocLibLoading);
  useRegisterEventListener('setIsDocLibEmpty', handleIsDocLibEmpty);
  useRegisterEventListener('setIsSsbciLoading', handleIsSsbciLoading);
  useRegisterEventListener('setIsSsbciEmpty', handleIsSsbciEmpty);
  useRegisterEventListener(
    'setIsMemberReferralsEmpty',
    handleIsMemberReferralsEmpty
  );
  useRegisterEventListener(
    'setIsMemberReferralsLoading',
    handleIsMemberReferralsLoading
  );

  useEffect(() => {
    let errorCount = 0;
    const countsBysectionEntries = Object.entries(countsBySection);
    countsBysectionEntries.forEach((section) => {
      if (section[1].error) {
        errorCount += 1;
      }
    });
    if (errorCount) {
      if (errorCount === countsBysectionEntries.length) {
        setShowFullError(true);
        setShowPartialError(false);
      } else {
        setShowFullError(false);
        setShowPartialError(true);
      }
    }
    if (!isEmpty(countsBySection)) {
      const calculatedCount = getTotalCounts(countsBySection, appPermissions);
      events.setTotalCount(calculatedCount);
    }
  }, [countsBySection]);

  const handleChange = (isChecked, providerChange = false) => {
    failedCallsRef.current = 0;
    if (!providerChange) {
      event('ACTION_REQUIRED_CHECK_BOX_CLICK', {
        interactionValue: !isRequiredOnlyChecked ? 'yes' : 'no',
      });
    }

    events.setActionRequiredFilter(!!isChecked);
    setIsRequiredOnlyChecked(isChecked);
  };

  useEffect(() => {
    handleChange(isRequiredOnlyChecked, true);
    setIsAllLoaded(false);
    const permissions = {
      admissionDischarge: validatePermissions(user, 'admissionDischarge.read'),
      claims: validatePermissions(user, 'claims.read'),
      doclib: validatePermissions(user, 'document-library.read'),
      mpp: validatePermissions(user, 'my-practice-profile.read'),
      paan: validatePermissions(user, 'prior-auth.read'),
      ssbci: validatePermissions(user, 'ssbci.read'),
      memberReferrals: validatePermissions(user, 'memberReferrals.read'),
      culturalCompetency: true,
    };
    setAppPermissions(permissions);
  }, [provider]);

  useEffect(() => {
    const claimsConditional =
      appPermissions.claims && renderClaimsParcel
        ? countsBySection.claims !== undefined && !isClaimsLoading
        : true;
    const memberReferralsConditional =
      appPermissions.claims && renderMemberReferralParcel
        ? countsBySection.memberReferrals !== undefined &&
          !isMemberReferralsLoading
        : true;
    const ssbciConditional =
      appPermissions.ssbci && renderSsbciParcel
        ? countsBySection.ssbci !== undefined && !isSsbciLoading
        : true;
    const docLibConditional =
      appPermissions.doclib && renderDoclibParcel
        ? countsBySection.doclib !== undefined && !isDocLibLoading
        : true;
    const admitDischargeErConditional =
      appPermissions.admissionDischarge &&
      getFeature('TRACKIT_ADMIT_DISCHARGE_ER') &&
      getFeature('TRACKIT_ADMISSION' || getFeature('TRACKIT_DISCHARGE')) &&
      renderDischargeEmergencyParcel
        ? countsBySection.admissionDischarge !== undefined &&
          !isAdmitDischargeErLoading
        : true;
    const paanConditional =
      appPermissions.paan && renderPaanParcel
        ? countsBySection.paan !== undefined && !isPaanLoading
        : true;
    const culturalConditional =
      appPermissions.culturalCompetency && renderCulturalParcel
        ? countsBySection.culturalCompetency !== undefined &&
          !isCulturalCompetencyLoading
        : true;
    if (
      Object.keys(countsBySection).length &&
      claimsConditional &&
      docLibConditional &&
      admitDischargeErConditional &&
      paanConditional &&
      !isMppLoading &&
      culturalConditional &&
      memberReferralsConditional &&
      ssbciConditional
    ) {
      events.setCallsCompleted();
    }
  }, [
    isClaimsLoading,
    isDocLibLoading,
    isAdmitDischargeErLoading,
    isMppLoading,
    isPaanLoading,
    countsBySection,
    appPermissions,
    isCulturalCompetencyLoading,
    isSsbciLoading,
    isMemberReferralsLoading,
  ]);

  const widgetProps = {
    onlyShowRequired: isRequiredOnlyChecked,
    provider,
    user,
    // setCountsBySection,
  };

  const isRenderClaimsAndCulturalCompetencyParcelsWidth =
    !renderClaimsParcel &&
    (!renderCulturalParcel || !isCulturalCompetencyToggle);

  const isCheckAdmissionDischargePaanDocLibParcelsEmpty =
    isAdmitDischargeEmpty && isPaanEmpty && isDocLibEmpty;

  const isRenderAdmissionDischargeMppPaanDocLibParcelsWidth =
    !renderDischargeEmergencyParcel &&
    !renderMppParcel &&
    (!renderPaanParcel || !isPaanToggle) &&
    (!renderDoclibParcel || isDocLibToggle);

  const isRequiredFilterCheckedWidthDischargePaanMppDocLib =
    (!isCulturalCompetencyEmpty && isClaimsEmpty && isRequiredOnlyChecked) ||
    isRenderClaimsAndCulturalCompetencyParcelsWidth
      ? '100%'
      : '50%';

  const isRequiredFilterCheckedWidthClaimsCulturalCompetency =
    (isCheckAdmissionDischargePaanDocLibParcelsEmpty &&
      isRequiredOnlyChecked) ||
    isRenderAdmissionDischargeMppPaanDocLibParcelsWidth
      ? '100%'
      : '50%';

  useEffect(() => {
    if (
      !isClaimsLoading &&
      !isDocLibLoading &&
      !isAdmitDischargeErLoading &&
      !isMppLoading &&
      !isPaanLoading &&
      !isCulturalCompetencyLoading &&
      !isSsbciLoading &&
      !isMemberReferralsLoading
    )
      setIsAllLoaded(true);
  }, [
    isClaimsLoading,
    isDocLibLoading,
    isAdmitDischargeErLoading,
    isMppLoading,
    isPaanLoading,
    isCulturalCompetencyLoading,
    isSsbciLoading,
    isMemberReferralsLoading,
  ]);

  useEffect(() => {
    if (isAllLoaded) {
      setIsRequiredOnlyChecked(true);
      events.setActionRequiredFilter(true);
    }
  }, [isAllLoaded]);

  useEffect(() => {
    const calculatedCount = getTotalCounts(countsBySection, appPermissions);
    const shouldShowCaughtUp = isRequiredOnlyChecked && calculatedCount === 0;

    if (shouldShowCaughtUp) {
      setShowAllCaughtUpText(true);
    } else {
      events.setActionRequiredFilter(isRequiredOnlyChecked);
      setShowAllCaughtUpText(false);
    }
  }, [isRequiredOnlyChecked, countsBySection, appPermissions]);

  return (
    <div>
      <StyledH4>Action Required</StyledH4>
      <Text>View and take action on items below that required attention.</Text>
      <Divider height="1" />
      <div>
        <If condition={!showFullError}>
          <StyledCheckbox
            isChecked={isRequiredOnlyChecked}
            onChange={(e) => handleChange(e.target.checked)}
            label="Show only items that require action"
          />
        </If>
        <If
          condition={
            !isRequiredOnlyChecked &&
            !showFullError &&
            showPartialError &&
            !isClaimsLoading &&
            !isDocLibLoading &&
            !isPaanLoading &&
            !isAdmitDischargeErLoading &&
            !isMppLoading &&
            !isCulturalCompetencyLoading
          }
        >
          <Alert
            variant="info"
            onAction={() => window.location.reload(false)}
            actionText="Refresh page"
            css={{
              'abyss-alert-container': {
                marginBottom: '2%',
              },
              'abyss-alert-header-text-container': {
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'space-between',
              },
              'abyss-alert-header-section': {
                width: '100%',
              },
              'abyss-alert-title-section': {
                width: '100%',
              },
              'abyss-alert-action-container': {
                marginLeft: '8px',
              },
            }}
          >
            One or more services are not responding. Please refresh the page or
            try again at a later time.
          </Alert>
        </If>

        <PaddedContainer>
          <LoadingOverlay
            isLoading={
              isClaimsLoading ||
              isDocLibLoading ||
              isPaanLoading ||
              isAdmitDischargeErLoading ||
              isMppLoading ||
              isCulturalCompetencyLoading
            }
            loadingTitle="Please wait while we retrieve"
            loadingMessage="Action Required items"
          >
            <If condition={showAllCaughtUpText}>
              <StyledVerticalCenter>
                <StyledIconMessageContainer>
                  <IconBrand
                    title="checkmark"
                    icon="checkmark"
                    variant="twotonelightcircle"
                    isScreenReadable
                    size={128}
                  />
                  <div>
                    <Label color="$primary1" size="$md">
                      All Caught Up
                    </Label>
                  </div>
                  <div>No items require action at this time.</div>
                </StyledIconMessageContainer>
              </StyledVerticalCenter>
            </If>

            <If condition={showFullError}>
              <StyledVerticalCenter>
                <StyledIconMessageContainer>
                  <IconBrand
                    title="alert"
                    icon="alert"
                    isScreenReadable
                    size={70}
                  />
                  <br />
                  <div>The system is unable to respond at the moment.</div>
                  <div>
                    Please try refreshing the page or try again at a later time.
                  </div>

                  <div>
                    <Button
                      variant="tertiary"
                      onClick={() => window.location.reload(false)}
                      fontWeight="bold"
                    >
                      Refresh Page
                    </Button>
                  </div>
                </StyledIconMessageContainer>
              </StyledVerticalCenter>
            </If>

            <Grid
              style={{
                display: showAllCaughtUpText || showFullError ? 'none' : null,
              }}
            >
              <If condition={renderClaimsParcel || renderCulturalParcel}>
                <Grid.Col
                  span={{
                    md: '100%',
                    lg:
                      appPermissions.admissionDischarge ||
                      appPermissions.mpp ||
                      appPermissions.paan ||
                      appPermissions.doclib
                        ? isRequiredFilterCheckedWidthClaimsCulturalCompetency
                        : '100%',
                  }}
                  style={{
                    display:
                      isRequiredOnlyChecked &&
                      isClaimsEmpty &&
                      isCulturalCompetencyEmpty &&
                      !renderClaimsParcel &&
                      !renderCulturalParcel
                        ? 'none'
                        : 'block',
                  }}
                >
                  <FlexDiv>
                    <If condition={appPermissions.claims && renderClaimsParcel}>
                      <div
                        style={{
                          display:
                            isRequiredOnlyChecked && isClaimsEmpty
                              ? 'none'
                              : 'block',
                        }}
                      >
                        <abyss-parcel
                          import="trackit-claims-action-required-widget@dev"
                          scriptdomain={trackitDomain}
                          onlyShowRequired={JSON.stringify(
                            widgetProps.onlyShowRequired
                          )}
                          provider={JSON.stringify(widgetProps.provider)}
                          user={JSON.stringify(widgetProps.user)}
                        />
                      </div>
                    </If>
                    <If condition={renderCulturalParcel}>
                      <div
                        style={{
                          display:
                            isRequiredOnlyChecked && isCulturalCompetencyEmpty
                              ? 'none'
                              : 'block',
                        }}
                      >
                        <abyss-parcel
                          import="trackit-cultural-competency-widget@dev"
                          scriptdomain={trackitDomain}
                          onlyShowRequired={JSON.stringify(
                            widgetProps.onlyShowRequired
                          )}
                          provider={JSON.stringify(widgetProps.provider)}
                          user={JSON.stringify(widgetProps.user)}
                        />
                      </div>
                    </If>
                  </FlexDiv>
                </Grid.Col>
              </If>
              <Grid.Col
                span={{
                  md: '100%',
                  lg: appPermissions.claims
                    ? isRequiredFilterCheckedWidthDischargePaanMppDocLib
                    : '100%',
                }}
              >
                <FlexDiv>
                  <If
                    condition={
                      appPermissions.admissionDischarge &&
                      renderDischargeEmergencyParcel
                    }
                  >
                    <div
                      style={{
                        display:
                          isRequiredOnlyChecked && isAdmitDischargeEmpty
                            ? 'none'
                            : 'block',
                      }}
                    >
                      <abyss-parcel
                        import="trackit-admission-discharge-emergency-widget@dev"
                        scriptdomain={trackitDomain}
                        onlyShowRequired={JSON.stringify(
                          widgetProps.onlyShowRequired
                        )}
                        provider={JSON.stringify(widgetProps.provider)}
                        user={JSON.stringify(widgetProps.user)}
                      />
                    </div>
                  </If>
                  <If condition={appPermissions.paan && renderPaanParcel}>
                    <div
                      style={{
                        display:
                          isRequiredOnlyChecked && isPaanEmpty
                            ? 'none'
                            : 'block',
                      }}
                    >
                      <abyss-parcel
                        import="trackit-paan-action-required-widget@dev"
                        scriptdomain={trackitDomain}
                        onlyShowRequired={JSON.stringify(
                          widgetProps.onlyShowRequired
                        )}
                        provider={JSON.stringify(widgetProps.provider)}
                        user={JSON.stringify(widgetProps.user)}
                      />
                    </div>
                  </If>
                  <If
                    condition={
                      appPermissions.claims && renderMemberReferralParcel
                    }
                  >
                    <div
                      style={{
                        display:
                          isRequiredOnlyChecked && isMemberReferralEmpty
                            ? 'none'
                            : 'block',
                      }}
                    >
                      <abyss-parcel
                        import="trackit-member-referrals-action-required-widget@dev"
                        scriptdomain={trackitDomain}
                        onlyShowRequired={JSON.stringify(
                          widgetProps.onlyShowRequired
                        )}
                        provider={JSON.stringify(widgetProps.provider)}
                        user={JSON.stringify(widgetProps.user)}
                      />
                    </div>
                  </If>
                  <If condition={appPermissions.ssbci && renderSsbciParcel}>
                    <div
                      style={{
                        display:
                          isRequiredOnlyChecked && isSsbciEmpty
                            ? 'none'
                            : 'block',
                      }}
                    >
                      <abyss-parcel
                        import="trackit-ssbci-action-required-widget@dev"
                        scriptdomain={trackitDomain}
                        onlyShowRequired={JSON.stringify(
                          widgetProps.onlyShowRequired
                        )}
                        provider={JSON.stringify(widgetProps.provider)}
                        user={JSON.stringify(widgetProps.user)}
                      />
                    </div>
                  </If>
                  <If condition={appPermissions.mpp && renderMppParcel}>
                    <MppSection
                      setIsMppLoading={setIsMppLoading}
                      {...widgetProps}
                      setCountsBySections={setCountsBySection}
                    />
                  </If>
                  <If condition={appPermissions.doclib && renderDoclibParcel}>
                    <div
                      style={{
                        display:
                          isRequiredOnlyChecked && isDocLibEmpty
                            ? 'none'
                            : 'block',
                      }}
                    >
                      <abyss-parcel
                        import="teamsview-action-required-widget@dev"
                        scriptdomain={docLibDomain}
                        onlyShowRequired={JSON.stringify(
                          widgetProps.onlyShowRequired
                        )}
                        provider={JSON.stringify(widgetProps.provider)}
                        user={JSON.stringify(widgetProps.user)}
                      />
                    </div>
                  </If>
                </FlexDiv>
              </Grid.Col>
            </Grid>
          </LoadingOverlay>
        </PaddedContainer>
      </div>
    </div>
  );
};
